import React, { useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { type ButtonProps } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ACTION_SUBJECT_PREFIX, EVENT_SOURCE } from '../../constants/analytics';

const styles = cssMap({
	base: {
		paddingTop: token('space.100'),
	},
	buttonText: {
		paddingLeft: token('space.025'),
	},
	contentExpanded: {
		paddingBlock: token('space.100'),
	},
	contentCollapsed: {
		paddingBlock: '0',
	},
});

interface ExpandProps {
	label: string;
	children: React.ReactNode;
	icon: ButtonProps['iconBefore'];
	attachmentType: 'file' | 'url';
	attachmentCount: number;
}

export function Expand({ children }: ExpandProps): React.JSX.Element {
	return <div>{children}</div>;
}

export const ExpandList = ({
	children,
	defaultExpandedIndex = -1,
	parentPosition,
	buttonType,
}: {
	children: React.ReactNode;
	defaultExpandedIndex?: number;
	parentPosition?: number;
	buttonType?: string;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [activeId, setActiveId] = useState<number>(defaultExpandedIndex);

	// Sync activeId when defaultExpandedIndex changes (e.g., when position changes due to filtering)
	useEffect(() => {
		setActiveId(defaultExpandedIndex);
	}, [defaultExpandedIndex]);

	const handleExpand = (expandId: number) =>
		setActiveId((prev) => (prev === expandId ? -1 : expandId));

	const childrenArray = React.Children.toArray(children)
		.filter((child): child is React.ReactElement<ExpandProps> =>
			React.isValidElement<ExpandProps>(child),
		)
		.filter((child) => child.props.attachmentCount > 0);

	return (
		<div>
			<Inline space="space.100">
				{childrenArray.map((child, index) => {
					const isExpanded = activeId === index;
					return (
						<Box xcss={styles.base}>
							<Button
								key={child.props.label}
								iconBefore={child.props.icon}
								onClick={() => {
									handleExpand(index);
									createAnalyticsEvent({
										type: 'sendUIEvent',
										data: {
											action: 'clicked',
											actionSubject: `${ACTION_SUBJECT_PREFIX}ExpandAttachmentButton`,
											source: EVENT_SOURCE,
											attributes: {
												attachmentType: child.props.attachmentType,
												attachmentCount: child.props.attachmentCount,
												position: parentPosition,
												buttonType: buttonType,
											},
										},
									}).fire();
								}}
								isSelected={isExpanded}
							>
								<Box xcss={styles.buttonText}>{child.props.label}</Box>
							</Button>
						</Box>
					);
				})}
			</Inline>
			<Box xcss={styles[activeId >= 0 ? 'contentExpanded' : 'contentCollapsed']}>
				{childrenArray[activeId]}
			</Box>
		</div>
	);
};
